/**
 * ChestSpawner: places chests in the maze at level start.
 *
 * Unlike food/coins/keys, chests are placed once when the maze is
 * generated, not on a timer. They persist until the player opens them.
 *
 * Placement rules:
 *  - No chests in levels 1 and 2 (KEY_CHEST_MIN_LEVEL = 3)
 *  - Max per tier per level: metallic=3, copper=2, gold=1
 *  - Actual counts are random (0 to max) with lower tiers more common
 *  - Chests avoid: start cell, exit cell, hazard cells, and each other
 *  - Gold chests are rarer than copper, which are rarer than metallic
 */

import ChestItem from '../entities/ChestItem.js';
import { CellType, ChestTier } from '../utils/constants.js';
import MazeGenerator from './MazeGenerator.js';
import {
  TILE_SIZE,
  CHEST_MAX_METALLIC,
  CHEST_MAX_COPPER,
  CHEST_MAX_GOLD,
  MIN_SPAWN_DISTANCE_FROM_PLAYER,
} from '../config/game-config.js';

export default class ChestSpawner {
  /**
   * @param {Phaser.Scene} scene
   * @param {number[][]} grid - Maze grid
   * @param {{ col: number, row: number }} startPos - Player start position
   * @param {{ col: number, row: number }} exitPos - Exit position
   * @param {Set<string>} forbiddenCells - Hazard cells to avoid
   */
  constructor(scene, grid, startPos, exitPos, forbiddenCells) {
    this.scene = scene;
    this.grid = grid;
    this.startPos = startPos;
    this.exitPos = exitPos;
    this.forbiddenCells = forbiddenCells;

    /** @type {ChestItem[]} All chest items in the current maze. */
    this.activeItems = [];
  }

  /**
   * Place chests in the maze. Call once at level start.
   *
   * @returns {ChestItem[]} Array of placed chest items
   */
  placeChests() {
    // Build list of candidate cells
    const occupied = new Set();
    occupied.add(`${this.startPos.col},${this.startPos.row}`);
    occupied.add(`${this.exitPos.col},${this.exitPos.row}`);

    // Also exclude cells adjacent to start (give player breathing room)
    const adjOffsets = [[-1, 0], [1, 0], [0, -1], [0, 1]];
    for (const [dc, dr] of adjOffsets) {
      occupied.add(`${this.startPos.col + dc},${this.startPos.row + dr}`);
    }

    const rows = this.grid.length;
    const cols = this.grid[0].length;

    // Collect all walkable path cells
    const pathCells = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.grid[r][c] === CellType.PATH) {
          const key = `${c},${r}`;
          if (!occupied.has(key) && !this.forbiddenCells.has(key)) {
            // Must be at least MIN_SPAWN_DISTANCE from start
            const dist = Math.abs(c - this.startPos.col) + Math.abs(r - this.startPos.row);
            if (dist >= MIN_SPAWN_DISTANCE_FROM_PLAYER + 2) {
              pathCells.push({ col: c, row: r });
            }
          }
        }
      }
    }

    // Shuffle candidate cells
    this._shuffle(pathCells);

    // Determine how many chests of each tier to place
    // Gold: 0 or 1 (weighted toward 0, ~40% chance of 1)
    // Copper: 0 to 2 (weighted toward fewer)
    // Metallic: 0 to 3 (weighted toward fewer)
    const goldCount = Math.random() < 0.35 ? 1 : 0;
    const copperCount = this._weightedRandom(CHEST_MAX_COPPER, 0.45);
    const metallicCount = this._weightedRandom(CHEST_MAX_METALLIC, 0.55);

    // Place in order: gold first (rarest, best placement), then copper, then metallic
    const placements = [
      { tier: ChestTier.GOLD, count: goldCount },
      { tier: ChestTier.COPPER, count: copperCount },
      { tier: ChestTier.METALLIC, count: metallicCount },
    ];

    let cellIndex = 0;

    for (const { tier, count } of placements) {
      for (let i = 0; i < count && cellIndex < pathCells.length; i++) {
        const cell = pathCells[cellIndex++];
        const x = cell.col * TILE_SIZE + TILE_SIZE / 2;
        const y = cell.row * TILE_SIZE + TILE_SIZE / 2;

        const chest = new ChestItem(this.scene, x, y, tier, cell.col, cell.row);
        this.activeItems.push(chest);

        // Mark this cell as occupied so no other chests land here
        occupied.add(`${cell.col},${cell.row}`);
      }
    }

    return this.activeItems;
  }

  /**
   * Get all chest positions as a Set of "col,row" strings.
   * Used by other spawners to avoid placing items on chest cells.
   *
   * @returns {Set<string>}
   */
  getChestCells() {
    const cells = new Set();
    for (const chest of this.activeItems) {
      cells.add(`${chest.gridCol},${chest.gridRow}`);
    }
    return cells;
  }

  /**
   * Find an unopened chest adjacent to the given position in any direction.
   * Checks all four cardinal directions (up, down, left, right) at both
   * 1 and 2 grid-cell distances to account for the expanded maze grid
   * where adjacent path cells are 2 grid units apart with a wall between.
   *
   * @param {number} col - Player grid column
   * @param {number} row - Player grid row
   * @returns {ChestItem|null} The nearest adjacent chest, or null
   */
  findAdjacentChest(col, row) {
    const directions = [
      { dc: 0, dr: -1 },  // up
      { dc: 0, dr: 1 },   // down
      { dc: -1, dr: 0 },  // left
      { dc: 1, dr: 0 },   // right
    ];

    for (const { dc, dr } of directions) {
      // Check 1 cell away (immediate neighbor)
      const nearCol = col + dc;
      const nearRow = row + dr;

      for (const chest of this.activeItems) {
        if (!chest.isOpen && chest.gridCol === nearCol && chest.gridRow === nearRow) {
          return chest;
        }
      }

      // Check 2 cells away (past a wall in expanded grid).
      // Only consider if the intermediate cell (wall slot) is a passage,
      // meaning the player can actually reach the chest.
      const farCol = col + dc * 2;
      const farRow = row + dr * 2;
      const wallCol = col + dc;
      const wallRow = row + dr;

      // Verify intermediate cell is walkable (passage between player and chest)
      if (!MazeGenerator.isWalkable(this.grid, wallCol, wallRow)) continue;

      for (const chest of this.activeItems) {
        if (!chest.isOpen && chest.gridCol === farCol && chest.gridRow === farRow) {
          return chest;
        }
      }
    }

    return null;
  }

  /**
   * Remove a chest from the active list.
   * @param {ChestItem} chest
   */
  removeChest(chest) {
    const idx = this.activeItems.indexOf(chest);
    if (idx !== -1) {
      this.activeItems.splice(idx, 1);
    }
  }

  /**
   * Destroy all chests and clean up.
   */
  destroy() {
    for (const chest of this.activeItems) {
      if (chest) chest.destroy();
    }
    this.activeItems = [];
  }

  /**
   * Serialize chest positions and tiers for save state.
   * @returns {Array<{col: number, row: number, tier: string}>}
   */
  serialize() {
    return this.activeItems
      .filter((c) => c && !c.isOpen)
      .map((c) => ({ col: c.gridCol, row: c.gridRow, tier: c.tier }));
  }

  /**
   * Restore chests from saved state.
   * @param {Array<{col: number, row: number, tier: string}>} data
   */
  restore(data) {
    if (!data || !Array.isArray(data)) return;

    for (const { col, row, tier } of data) {
      const x = col * TILE_SIZE + TILE_SIZE / 2;
      const y = row * TILE_SIZE + TILE_SIZE / 2;
      const chest = new ChestItem(this.scene, x, y, tier, col, row);
      this.activeItems.push(chest);
    }
  }

  // ---------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------

  /**
   * Fisher-Yates shuffle (in-place).
   * @private
   * @param {any[]} arr
   */
  _shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }

  /**
   * Generate a weighted random count from 0 to max,
   * biased toward lower values.
   *
   * @private
   * @param {number} max
   * @param {number} probability - Base probability per attempt
   * @returns {number}
   */
  _weightedRandom(max, probability) {
    let count = 0;
    for (let i = 0; i < max; i++) {
      if (Math.random() < probability) {
        count++;
      }
      // Each subsequent chest is less likely
      probability *= 0.7;
    }
    return count;
  }
}
